import { useQuery } from '@tanstack/react-query';
import { Users, Building2, Layers, Share2, FileText, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { adminApi } from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';

export function AdminOverview() {
  const { getToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.stats(token!);
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  const t = data?.totals;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Users" value={t?.users ?? 0} icon={Users} color="text-info" bg="bg-info/10" />
        <StatCard label="Superadmins" value={t?.superadmins ?? 0} icon={ShieldCheck} color="text-warning" bg="bg-warning/10" />
        <StatCard label="Agencies" value={t?.agencies ?? 0} icon={Building2} color="text-info" bg="bg-info/10" />
        <StatCard label="Workspaces" value={t?.workspaces ?? 0} icon={Layers} color="text-accent" bg="bg-accent/10" />
        <StatCard label="Social Accounts" value={t?.social_accounts ?? 0} icon={Share2} color="text-accent" bg="bg-accent/10" />
        <StatCard label="Posts" value={t?.posts ?? 0} icon={FileText} color="text-success" bg="bg-success/10" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Posts by status */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Posts by Status</h3>
          {(data?.postsByStatus.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No posts yet</p>
          ) : (
            <div className="space-y-2">
              {data!.postsByStatus.map((row) => (
                <div key={row.status} className="flex items-center justify-between text-xs">
                  <span className="text-foreground capitalize">{row.status}</span>
                  <span className="text-muted-foreground font-medium">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Accounts by platform */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Social Accounts by Platform</h3>
          {(data?.accountsByPlatform.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No connected accounts</p>
          ) : (
            <div className="space-y-2">
              {data!.accountsByPlatform.map((row) => (
                <div key={row.platform} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-foreground">
                    <PlatformIcon platform={row.platform} variant="glyph" size="xs" />
                    {PLATFORMS[row.platform as keyof typeof PLATFORMS]?.label ?? row.platform}
                  </span>
                  <span className="text-muted-foreground font-medium">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bg: string;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className={`h-7 w-7 rounded-md ${bg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
